import * as _ from 'lodash';
import * as qs from 'qs';

import { nuc } from '../../../../utils';
import { icons } from '../../../generic';
import type { AttributionReportIncludeOptions as ReportOptions } from '../../../../../shared/reports';
import type {
  DependencyInfoOption,
  FixPlanReportOptionDetail,
  FixPlanReportOptions,
  ProjectContext,
  ProjectGroupContext,
  ReportAction,
  ReportCategory,
  ReportContext,
  ReportExclusionOptionDetail,
  ReportExclusionOptions,
  ReportFormatDetail,
  ReportFormatType,
  ReportOptionDetail,
  ReportURLParams,
} from '../shared/types';

/**
 * Constants
 */

export const ProjectType = {
  Project: 'project',
  ReleaseGroup: 'releaseGroup',
} as const;

// Categories
export const ReportCategories = {
  Licensing: 'licensing',
  Vulnerability: 'vulnerability',
  SBOM: 'sbom',
  RemediationGuidance: 'remediation-guidance',
} as const;

export const ReportCategoryTitles: Record<ReportCategory, string> = {
  licensing: 'Licensing',
  vulnerability: 'Vulnerability',
  sbom: 'SBOM',
  'remediation-guidance': 'Remediation Guidance',
} as const;

// Formats
export const ReportFormats = {
  // Licensing
  HTML: 'HTML',
  MD: 'MD',
  CSV: 'CSV',
  TXT: 'TXT',
  // Vulnerability + Licensing
  PDF: 'PDF',
  // SBOM
  SPDX: 'SPDX',
  SPDX_JSON: 'SPDX_JSON',
  CYCLONEDX_JSON: 'CYCLONEDX_JSON',
  CYCLONEDX_XML: 'CYCLONEDX_XML',
  // Fix Plans
  JSON: 'JSON',
} as const;

export const ReportFormatDetails: Record<ReportFormatType, ReportFormatDetail> = {
  [ReportFormats.HTML]: {
    key: ReportFormats.HTML,
    name: 'HTML',
    icon: icons.html5,
    type: [ReportCategories.Licensing],
    supportsReleaseGroups: true,
  },
  [ReportFormats.MD]: {
    key: ReportFormats.MD,
    name: 'MARKDOWN',
    icon: icons.fileWordO,
    type: [ReportCategories.Licensing],
    supportsReleaseGroups: true,
  },
  [ReportFormats.PDF]: {
    key: ReportFormats.PDF,
    name: 'PDF',
    icon: icons.filePdfO,
    type: [ReportCategories.Licensing, ReportCategories.Vulnerability],
    supportsReleaseGroups: true,
  },
  [ReportFormats.CSV]: {
    key: ReportFormats.CSV,
    name: 'CSV',
    icon: icons.fileExcelO,
    type: [ReportCategories.Licensing],
    supportsReleaseGroups: true,
  },
  [ReportFormats.TXT]: {
    key: ReportFormats.TXT,
    name: 'PLAIN TEXT',
    icon: icons.fileO,
    type: [ReportCategories.Licensing],
    supportsReleaseGroups: true,
  },
  [ReportFormats.SPDX]: {
    key: ReportFormats.SPDX,
    name: 'SPDX',
    icon: icons.fileTextO,
    type: [ReportCategories.SBOM],
    supportsReleaseGroups: true,
  },
  [ReportFormats.SPDX_JSON]: {
    key: ReportFormats.SPDX_JSON,
    name: 'SPDX (JSON)',
    icon: icons.fileTextO,
    type: [ReportCategories.SBOM],
    supportsReleaseGroups: true,
  },
  [ReportFormats.CYCLONEDX_JSON]: {
    key: ReportFormats.CYCLONEDX_JSON,
    name: 'CYCLONEDX (JSON)',
    icon: icons.fileTextO,
    type: [ReportCategories.SBOM],
    supportsReleaseGroups: true,
  },
  [ReportFormats.CYCLONEDX_XML]: {
    key: ReportFormats.CYCLONEDX_XML,
    name: 'CYCLONEDX (XML)',
    icon: icons.fileTextO,
    type: [ReportCategories.SBOM],
    supportsReleaseGroups: true,
  },
  [ReportFormats.JSON]: {
    key: ReportFormats.JSON,
    name: 'JSON',
    icon: icons.fileTextO,
    type: [ReportCategories.RemediationGuidance],
    supportsReleaseGroups: true,
  },
};

// Actions
export const ReportActions = {
  Preview: 'preview',
  Download: 'download',
  Email: 'email',
  Public: 'public',
  Publish: 'publish',
};

export const ReportOptionDetails: Record<keyof ReportOptions, ReportOptionDetail> = {
  includeConcludedLicenses: {
    key: 'includeConcludedLicenses',
    title: 'Concluded Licenses',
    description: `Licenses you've declared as governing the project`,
    category: [ReportCategories.Licensing],
    format: _.without(Object.values(ReportFormats), ReportFormats.CSV),
  },
  includeProjectLicense: {
    key: 'includeProjectLicense',
    title: 'Project Declared License',
    description: 'The license of the project analyzed by FOSSA',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
  includeLicenseScan: {
    key: 'includeLicenseScan',
    title: 'First Party Licenses',
    description: 'Licenses found within your code.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
  includeDependencySummary: {
    key: 'includeDependencySummary',
    title: 'Dependency Summary',
    description: 'Overview of all packages included in your project and their license information.',
    category: [ReportCategories.Licensing],
    format: _.without(Object.values(ReportFormats), ReportFormats.CSV),
  },
  includeDirectDependencies: {
    key: 'includeDirectDependencies',
    title: 'Direct Dependencies',
    description: 'Packages that your project explicitly depends on.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
  includeDeepDependencies: {
    key: 'includeDeepDependencies',
    title: 'Transitive Dependencies',
    description: 'Packages indirectly included through other dependencies.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
  includeSnippetDependencies: {
    key: 'includeSnippetDependencies',
    title: 'Snippet Dependencies',
    description: 'Code fragments identified through snippet scanning that indicate use of external dependencies.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
  includeFOSSADependencies: {
    key: 'includeFOSSADependencies',
    title: 'FOSSA Projects',
    description: 'Include FOSSA Dependencies in Release Group Reports.',
    category: [ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
  includeLicenseList: {
    key: 'includeLicenseList',
    title: 'Full License List',
    description: 'Complete list of all licenses found across your project\'s dependencies.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: _.without(Object.values(ReportFormats), ReportFormats.CSV),
  },
  includeFileMatches: {
    key: 'includeFileMatches',
    title: 'License File Matches',
    description:
      'File paths within each dependency where license matches were detected.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: [ReportFormats.CSV, ReportFormats.TXT, ReportFormats.SPDX, ReportFormats.SPDX_JSON],
  },
  includeOpenVulnerabilities: {
    key: 'includeOpenVulnerabilities',
    title: 'Open Vulnerabilities',
    description:
      'Known security vulnerabilities that have not yet been resolved or ignored.',
    category: [ReportCategories.SBOM],
    format: [ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML],
  },
  includeClosedVulnerabilities: {
    key: 'includeClosedVulnerabilities',
    title: 'Closed Vulnerabilities',
    description:
      'Security vulnerabilities that have been resolved or ignored, including their resolution status.',
    category: [ReportCategories.SBOM],
    format: [ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML],
  },
  includeSupportStatus: {
    key: 'includeSupportStatus',
    title: 'Support Status',
    description:
      'Whether each dependency is actively maintained or has been identified as abandoned.',
    category: [ReportCategories.SBOM],
    format: [ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML],
  },
  includeLicenseHeaders: {
    key: 'includeLicenseHeaders',
    title: 'License Headers',
    description: 'License information found in the header of a source file.',
    category: [ReportCategories.Licensing],
    format: [ReportFormats.TXT],
  },
  includeCopyrightList: {
    key: 'includeCopyrightList',
    title: 'Copyrights',
    description: 'Ownership statements found in source files or license documents.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: _.without(Object.values(ReportFormats), ReportFormats.CSV),
  },
  includeNonLicenseCopyrights: {
    key: 'includeNonLicenseCopyrights',
    title: 'Copyrights from non-license text',
    description:
      'Copyright statements extracted from source code or documentation outside of license files.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: _.without(Object.values(ReportFormats), ReportFormats.CSV),
  },
  includeNoticeFiles: {
    key: 'includeNoticeFiles',
    title: 'Notice Files',
    description: 'Files that summarize license and copyright notices for a package.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: _.without(Object.values(ReportFormats), ReportFormats.CSV),
  },
  includeHashAndVersionData: {
    key: 'includeHashAndVersionData',
    title: 'Hash and Version Data',
    description: 'Unique identifiers and version details used to verify the exact package instance.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
  includePackageLabels: {
    key: 'includePackageLabels',
    title: 'Package Labels',
    description: 'Tags applied to packages to group, track, or filter dependencies.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
};

/**
 * This is a list of exclusion filters that are available for the report.
 * It is used to determine which filters to show in the UI and to build the URL params for the report.
 *
 * As more exclusion filters are added, this list as well as the
 * app/components/projects/generic/ReportGenerationV2/shared/components/Filter/exclusions/ExclusionGroup
 * component will need to be updated to support them.
 */
export const ReportOptionExclusionDetails: Record<keyof ReportExclusionOptions, ReportExclusionOptionDetail> = {
  packageLabels: {
    key: 'packageLabels',
    title: 'Package Labels',
    description: 'Labels associated with a project\'s dependencies',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
    scope: 'project',
  },
};

export const FixPlanReportOptionDetails: Record<keyof FixPlanReportOptions, FixPlanReportOptionDetail> = {
  includeQuickWins: {
    key: 'includeQuickWins',
    title: 'Quick Wins',
    alternativeTitle: 'Fix Quickly',
    description: 'Non-breaking changes that reduce the highest number of vulnerabilities with minimal effort.',
  },
  includeHighPriority: {
    key: 'includeHighPriority',
    title: 'High Priority Fixes',
    alternativeTitle: 'Prioritize Now',
    description: 'High-impact upgrades prioritized by risk signals such as exploitability and EPSS score.',
  },
  includeLowPriority: {
    key: 'includeLowPriority',
    title: 'Low Priority Fixes',
    alternativeTitle: 'Prioritize Next',
    description: 'Lower-risk upgrades identified by scoring signals such as exploitability and EPSS score.',
  },
  includeOutdatedDependencies: {
    key: 'includeOutdatedDependencies',
    title: 'Outdated Dependencies',
    alternativeTitle: 'Address Proactively',
    description: 'Dependencies that are multiple minor or major versions behind the latest release.',
  },
  includeMalware: {
    key: 'includeMalware',
    title: 'Malicious Dependencies',
    alternativeTitle: 'Malicious Dependencies',
    description: 'Dependencies identified as malicious and recommended for immediate removal.',
  },
  deduplicateOutdatedDeps: {
    key: 'deduplicateOutdatedDeps',
    title: 'Exclude Vulnerable',
    alternativeTitle: 'Exclude Vulnerable',
    description: 'Exclude fixes that do not address known vulnerabilities from the remediation plan.',
    isNested: true,
  },
  includeTransitiveVulns: {
    key: 'includeTransitiveVulns',
    title: 'Transitive Vulnerabilities',
    alternativeTitle: 'Transitive Vulnerabilities',
    description: 'Vulnerabilities introduced by indirect (transitive) dependencies rather than direct ones.',
  },
};

export const DefaultDependencyInfoOptions: DependencyInfoOption[] = [
  'Library',
  'License',
  'CustomTextLicense',
  'OtherLicenses',
];

export const FixPlanInfoOptions = {
  HighPriority: 'High Priority',
  LowPriority: 'Low Priority',
  QuickWins: 'Quick Wins',
  OutdatedDeps: 'Outdated Dependencies',
} as const;

/**
 * URL Generation
 */
export const projectReportBaseURL = (revision: string) => `/api/revisions/${nuc(revision)}/attribution`;
export const groupReportBaseURL = (groupId: number, releaseId: number) =>
  `/api/project_group/${groupId}/release/${releaseId}/attribution`;

export const buildURLParams = (options: ReportURLParams) => qs.stringify(options);

export const getReportURL = (action: ReportAction, context: ReportContext, options: ReportURLParams) => {
  const { projectType, category } = context;

  // Translate actions to parameters and add options
  const reportOptions = {
    ...(action === ReportActions.Preview ? { preview: true } : {}),
    ...(action === ReportActions.Download ? { download: true } : {}),
    ...(action === ReportActions.Publish ? { isPublishing: true } : {}),
    ...options,
  };

  // Get Base URL depending on project type
  let baseUrl;
  if (projectType === ProjectType.Project) {
    const { revision } = context as ProjectContext;

    baseUrl = projectReportBaseURL(revision);
  } else {
    const { groupId, releaseId } = context as ProjectGroupContext;

    baseUrl = groupReportBaseURL(groupId, releaseId);
  }

  const urlParams = buildURLParams(reportOptions);

  // Security URL has a different format from licensing
  if (category === ReportCategories.Vulnerability) {
    return `${baseUrl}/security?${urlParams}`;
  }

  // Release group URL is slightly different from licensing
  if (projectType === ProjectType.ReleaseGroup) {
    return `${baseUrl}/${options.format}?${urlParams}`;
  }

  return `${baseUrl}/${action}?${urlParams}`;
};
